import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Camera, Search, CheckCircle2, XCircle, User, Ticket, QrCode, Loader2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

interface PurchaseItem {
  id: string;
  attendee_name: string | null;
  checked_in: boolean;
  checked_in_at: string | null;
  ticket_price_tiers: {
    name: string;
    price: number;
  } | null;
  extras: {
    quantity: number;
    ticket_extras: {
      name: string;
    } | null;
  }[];
}

interface Purchase {
  id: string;
  qr_code: string;
  buyer_name: string;
  buyer_email: string;
  total_amount: number;
  payment_status: string;
  created_at: string;
  items: PurchaseItem[];
}

interface Event {
  id: string;
  name: string;
  event_date: string;
  pta_id: string;
}

export default function EventCheckin() {
  const { eventId: urlEventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [availableEvents, setAvailableEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(urlEventId || null);
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scannedPurchase, setScannedPurchase] = useState<Purchase | null>(null);
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, checkedIn: 0 });

  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerRef = useRef<HTMLDivElement>(null);

  // Fetch available ticketed events for the user's PTA
  useEffect(() => {
    const fetchAvailableEvents = async () => {
      if (!user?.id) return;

      try {
        // Get user's PTA
        const { data: memberData } = await supabase
          .from("members")
          .select("pta_id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!memberData?.pta_id) {
          // Try by email
          const { data: emailData } = await supabase
            .from("members")
            .select("pta_id")
            .eq("email", user.email)
            .maybeSingle();

          if (!emailData?.pta_id) {
            setLoading(false);
            return;
          }
          memberData.pta_id = emailData.pta_id;
        }

        // Fetch ticketed events for this PTA
        const { data: events, error } = await supabase
          .from("events")
          .select("id, name, event_date, pta_id")
          .eq("pta_id", memberData.pta_id)
          .eq("is_ticketed", true)
          .eq("is_archived", false)
          .gte("event_date", new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]) // Include events from last 7 days
          .order("event_date", { ascending: true });

        if (error) throw error;
        setAvailableEvents(events || []);

        // If no event selected but we have events, select the first one
        if (!selectedEventId && events && events.length > 0) {
          setSelectedEventId(events[0].id);
        }
      } catch (error) {
        console.error("Error fetching available events:", error);
      } finally {
        if (!selectedEventId) {
          setLoading(false);
        }
      }
    };

    fetchAvailableEvents();
  }, [user?.id, user?.email]);

  // Fetch selected event details
  useEffect(() => {
    if (!selectedEventId) {
      setEvent(null);
      setStats({ total: 0, checkedIn: 0 });
      return;
    }
    fetchEvent();
    fetchStats();
  }, [selectedEventId]);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const fetchEvent = async () => {
    if (!selectedEventId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, event_date, pta_id")
        .eq("id", selectedEventId)
        .single();

      if (error) throw error;
      setEvent(data);
    } catch (error) {
      console.error("Error fetching event:", error);
      toast.error("Failed to load event");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data: items } = await supabase
        .from("ticket_purchase_items")
        .select(`
          checked_in,
          ticket_purchases!inner (event_id)
        `)
        .eq("ticket_purchases.event_id", selectedEventId);

      if (items) {
        setStats({
          total: items.length,
          checkedIn: items.filter((i: any) => i.checked_in).length,
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const startScanner = async () => {
    if (!scannerContainerRef.current) return;

    try {
      setScanning(true);
      const html5QrCode = new Html5Qrcode("qr-reader");
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        async (decodedText) => {
          console.log("QR Code scanned:", decodedText);
          await stopScanner();
          await lookupPurchase(decodedText);
        },
        (errorMessage) => {
          // Ignore scan errors - they happen continuously when no QR is visible
        }
      );
    } catch (error) {
      console.error("Error starting scanner:", error);
      toast.error("Failed to start camera. Please check permissions.");
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
      } catch (error) {
        console.error("Error stopping scanner:", error);
      }
    }
    setScanning(false);
  };

  const lookupPurchase = async (qrCode: string) => {
    try {
      // Fetch purchase by QR code
      const { data: purchase, error: purchaseError } = await supabase
        .from("ticket_purchases")
        .select("*")
        .eq("qr_code", qrCode)
        .eq("event_id", selectedEventId)
        .single();

      if (purchaseError || !purchase) {
        toast.error("Ticket not found for this event");
        return;
      }

      // Fetch items with extras
      const { data: items, error: itemsError } = await supabase
        .from("ticket_purchase_items")
        .select(`
          *,
          ticket_price_tiers:price_tier_id (name, price)
        `)
        .eq("purchase_id", purchase.id);

      if (itemsError) throw itemsError;

      // Fetch extras for each item
      const itemsWithExtras = await Promise.all(
        (items || []).map(async (item) => {
          const { data: extras } = await supabase
            .from("ticket_purchase_item_extras")
            .select(`
              quantity,
              ticket_extras:extra_id (name)
            `)
            .eq("purchase_item_id", item.id);
          return { ...item, extras: extras || [] };
        })
      );

      setScannedPurchase({
        ...purchase,
        items: itemsWithExtras,
      });
    } catch (error) {
      console.error("Error looking up purchase:", error);
      toast.error("Failed to lookup ticket");
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    await lookupPurchase(searchQuery.trim());
  };

  const resetForNextCheckin = () => {
    setTimeout(() => {
      setScannedPurchase(null);
      setSearchQuery("");
    }, 5000);
  };

  const handleCheckIn = async (itemId: string) => {
    setCheckingIn(itemId);
    try {
      const { error } = await supabase
        .from("ticket_purchase_items")
        .update({
          checked_in: true,
          checked_in_at: new Date().toISOString(),
          checked_in_by: user?.id,
        })
        .eq("id", itemId);

      if (error) throw error;

      // Update local state
      if (scannedPurchase) {
        const updatedItems = scannedPurchase.items.map((item) =>
          item.id === itemId
            ? { ...item, checked_in: true, checked_in_at: new Date().toISOString() }
            : item
        );
        setScannedPurchase({
          ...scannedPurchase,
          items: updatedItems,
        });

        // Check if all items are now checked in
        const allCheckedIn = updatedItems.every((item) => item.checked_in);
        if (allCheckedIn) {
          resetForNextCheckin();
        }
      }

      toast.success("Attendee checked in!");
      fetchStats();
    } catch (error) {
      console.error("Error checking in:", error);
      toast.error("Failed to check in");
    } finally {
      setCheckingIn(null);
    }
  };

  const handleCheckInAll = async () => {
    if (!scannedPurchase) return;

    const uncheckedItems = scannedPurchase.items.filter((i) => !i.checked_in);
    if (uncheckedItems.length === 0) {
      toast.info("All attendees already checked in");
      return;
    }

    setCheckingIn("all");
    try {
      const { error } = await supabase
        .from("ticket_purchase_items")
        .update({
          checked_in: true,
          checked_in_at: new Date().toISOString(),
          checked_in_by: user?.id,
        })
        .in(
          "id",
          uncheckedItems.map((i) => i.id)
        );

      if (error) throw error;

      setScannedPurchase({
        ...scannedPurchase,
        items: scannedPurchase.items.map((item) => ({
          ...item,
          checked_in: true,
          checked_in_at: item.checked_in_at || new Date().toISOString(),
        })),
      });

      toast.success(`${uncheckedItems.length} attendee(s) checked in!`);
      fetchStats();
      resetForNextCheckin();
    } catch (error) {
      console.error("Error checking in all:", error);
      toast.error("Failed to check in attendees");
    } finally {
      setCheckingIn(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (availableEvents.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">No Ticketed Events</h1>
          <p className="text-muted-foreground mb-4">There are no upcoming ticketed events available for check-in.</p>
          <Button onClick={() => navigate("/dashboard/events")}>Go to Events Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">Event Check-in</h1>
              <Select
                value={selectedEventId || ""}
                onValueChange={(value) => {
                  setSelectedEventId(value);
                  setScannedPurchase(null);
                  setSearchQuery("");
                }}
              >
                <SelectTrigger className="w-full max-w-xs mt-2">
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent className="bg-popover z-50">
                  {availableEvents.map((evt) => (
                    <SelectItem key={evt.id} value={evt.id}>
                      <div className="flex flex-col">
                        <span>{evt.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(evt.event_date).toLocaleDateString()}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {event && (
              <div className="text-right">
                <div className="text-2xl font-bold">{stats.checkedIn}/{stats.total}</div>
                <p className="text-xs text-muted-foreground">Checked in</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {!event ? (
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Select an event to start check-in</p>
        </div>
      ) : (

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Scanner Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Scan Ticket
            </CardTitle>
            <CardDescription>
              {isMobile
                ? "Tap to scan a QR code or enter the code manually"
                : "Use your webcam to scan or enter the code manually"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* QR Scanner */}
            <div
              id="qr-reader"
              ref={scannerContainerRef}
              className={`w-full aspect-square max-w-[300px] mx-auto bg-muted rounded-lg overflow-hidden ${
                scanning ? "" : "hidden"
              }`}
            />

            {!scanning && (
              <Button
                onClick={startScanner}
                className="w-full"
                size="lg"
              >
                <Camera className="h-5 w-5 mr-2" />
                {isMobile ? "Scan QR Code" : "Use Webcam to Scan"}
              </Button>
            )}

            {scanning && (
              <Button
                onClick={stopScanner}
                variant="outline"
                className="w-full"
              >
                Stop Scanning
              </Button>
            )}

            {/* Manual Entry */}
            <Input
              placeholder="Enter 6-digit code..."
              value={searchQuery}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setSearchQuery(value);
                if (value.length === 6) {
                  lookupPurchase(value);
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Scanned Purchase Details */}
        {scannedPurchase && (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    {scannedPurchase.buyer_name}
                  </CardTitle>
                  <CardDescription>{scannedPurchase.buyer_email}</CardDescription>
                </div>
                <Badge
                  className={
                    scannedPurchase.payment_status === "completed" || scannedPurchase.payment_status === "paid"
                      ? "bg-green-500 hover:bg-green-600"
                      : ""
                  }
                  variant={
                    scannedPurchase.payment_status === "completed" || scannedPurchase.payment_status === "paid"
                      ? "default"
                      : "destructive"
                  }
                >
                  {scannedPurchase.payment_status === "completed" || scannedPurchase.payment_status === "paid"
                    ? "Paid"
                    : scannedPurchase.payment_status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Check In All Button */}
              {scannedPurchase.items.some((i) => !i.checked_in) && (
                <Button
                  onClick={handleCheckInAll}
                  className="w-full"
                  size="lg"
                  disabled={checkingIn === "all"}
                >
                  {checkingIn === "all" ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Check In All ({scannedPurchase.items.filter((i) => !i.checked_in).length})
                </Button>
              )}

              {/* Individual Tickets */}
              <div className="space-y-3">
                {scannedPurchase.items.map((item, index) => (
                  <div
                    key={item.id}
                    className={`p-4 rounded-lg border ${
                      item.checked_in ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800" : "bg-card"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Ticket className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {item.ticket_price_tiers?.name || "Ticket"} #{index + 1}
                          </span>
                        </div>
                        {item.attendee_name && (
                          <div className="text-sm font-semibold mb-1">
                            {item.attendee_name}
                          </div>
                        )}
                        {item.extras && item.extras.length > 0 && (
                          <div className="text-sm text-muted-foreground">
                            {item.extras.map((extra, i) => (
                              <div key={i}>
                                + {extra.ticket_extras?.name} (x{extra.quantity})
                              </div>
                            ))}
                          </div>
                        )}
                        {item.checked_in && item.checked_in_at && (
                          <div className="text-xs text-green-600 dark:text-green-400 mt-2">
                            Checked in at{" "}
                            {new Date(item.checked_in_at).toLocaleTimeString()}
                          </div>
                        )}
                      </div>
                      <div>
                        {item.checked_in ? (
                          <CheckCircle2 className="h-6 w-6 text-green-600" />
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleCheckIn(item.id)}
                            disabled={checkingIn === item.id}
                          >
                            {checkingIn === item.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Check In"
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Clear Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setScannedPurchase(null);
                  setSearchQuery("");
                }}
              >
                Scan Another Ticket
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      )}
    </div>
  );
}
